import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

/** Les trois langues du produit ; le rapport n'en connaît pas d'autres. */
export type ReportLang = 'ar' | 'fr' | 'en';

/** Sens de lecture, tel que l'attend l'attribut `dir` du DOM. */
export type ReportDir = 'rtl' | 'ltr';

/** Racine ajoutée aux fichiers `assets/i18n/*.json` lors du portage du modèle. */
const REPORT_NAMESPACE = 'REPORT';

const SUPPORTED_LANGS: readonly ReportLang[] = ['ar', 'fr', 'en'];

/**
 * Locales de formatage des nombres, reprises telles quelles du modèle.
 *
 * `ar-MA-u-nu-latn` force les **chiffres latins** en arabe : ce n'est pas un
 * oubli. Le rapport aligne des codes de compétences, des notes et des
 * pourcentages dans des tableaux et des graphiques ; les chiffres arabo-indiens
 * (٠١٢…) y cassaient l'alignement et la lecture des axes. Le choix a été validé
 * sur le document imprimé — ne pas le « corriger ».
 */
const NUMERIC_LOCALE: Record<ReportLang, string> = {
  ar: 'ar-MA-u-nu-latn',
  fr: 'fr-FR',
  en: 'en-GB',
};

/**
 * Mois en arabe maghrébin (جانفي, فيفري, أفريل…), pas les noms du Machrek que
 * renverrait `Intl` pour la locale `ar`. C'est le vocabulaire du public visé,
 * d'où le contournement d'`Intl` pour la date en arabe.
 */
const AR_MONTHS = [
  'جانفي',
  'فيفري',
  'مارس',
  'أفريل',
  'ماي',
  'جوان',
  'جويلية',
  'أوت',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

/**
 * Moteur i18n du rapport de diagnostic — portage fidèle de celui que porte le
 * document autonome (`../mocks/report/js/script.js`).
 *
 * **Pourquoi un moteur séparé plutôt que `TranslateService` seul ?**
 *
 * 1. **Les pluriels.** ngx-translate n'en a pas. Or les libellés du rapport
 *    comptent des compétences et des minutes, et l'arabe distingue réellement
 *    six catégories (zéro, un, deux, quelques, beaucoup, autre) : « 0 مهارة »,
 *    « مهارتان », « 3 مهارات » ne s'écrivent pas de la même façon. On rejoue donc
 *    la sélection de `Intl.PluralRules` comme le fait le modèle.
 *
 * 2. **Le repli anglais.** La chaîne du modèle est *langue courante → anglais →
 *    la clé*. Celle de ngx-translate est *langue courante → langue par défaut*,
 *    et la langue par défaut du produit est l'**arabe**. Laisser `instant()`
 *    mener la recherche produirait du texte arabe au milieu d'un rapport
 *    anglais : en anglais, `count === 0` demande la catégorie `other`, absente
 *    des bundles ; ngx-translate irait alors chercher `…_zero` dans l'arabe et
 *    le trouverait. Les candidats de pluriel sont donc résolus directement dans
 *    le bundle de la langue courante, puis dans celui de l'anglais.
 *
 * 3. **Les variables typées par leur forme.** Le modèle écrit
 *    `t('summary.score', { score: { mark: 9 } })` : la forme de l'objet dit
 *    comment formater. Cela évite d'avoir à formater à l'appel, et surtout de
 *    formater différemment d'un écran à l'autre.
 *
 * Les libellés, les pluriels et la forme des nombres ont été validés tels
 * quels sur le document remis à l'élève : ce service les reproduit à
 * l'identique, il ne les réinterprète pas.
 */
@Injectable({ providedIn: 'root' })
export class ReportI18nService {
  private readonly translate = inject(TranslateService);

  /**
   * Langue active du rapport.
   *
   * Alimentée par `onLangChange` pour que tout ce qui en dérive (le sens de
   * lecture, les libellés, les nombres) se recalcule à la bascule sans qu'un
   * composant ait à s'abonner. `currentLang` peut être vide tant que le premier
   * `use()` n'a pas abouti, d'où l'arabe — langue par défaut du produit — en
   * valeur initiale.
   */
  readonly lang: Signal<ReportLang> = toSignal(
    this.translate.onLangChange.pipe(
      map((event: LangChangeEvent) => normalizeLang(event.lang)),
    ),
    { initialValue: normalizeLang(this.translate.currentLang) },
  );

  /** L'arabe est la seule langue RTL du produit. */
  readonly dir: Signal<ReportDir> = computed(() =>
    this.lang() === 'ar' ? 'rtl' : 'ltr',
  );

  /* ── Traduction ──────────────────────────────────────────────── */

  /**
   * Libellé du rapport dans la langue active.
   *
   * La clé est **relative** : le service préfixe par `REPORT.`, si bien que les
   * gabarits et les composants gardent les clés courtes du modèle
   * (`t('summary.score')`) et que le portage n'a rien eu à réécrire.
   *
   * `vars.count` déclenche la sélection de pluriel ; les autres variables sont
   * formatées d'après leur forme (voir {@link resolveVars}).
   */
  t(key: string, vars?: Record<string, unknown>): string {
    const lang = this.lang();
    return this.translateIn(lang, key, this.resolveVars(lang, vars));
  }

  /* ── Formats ─────────────────────────────────────────────────── */

  /** Nombre entier par défaut ; `digits` fixe les décimales affichées. */
  num(v: number, digits?: number): string {
    return this.fmtNum(v, this.lang(), digits);
  }

  /** Pourcentage : la valeur est déjà sur 100 (43 → « 43 % »). */
  pct(v: number, digits?: number): string {
    return this.fmtPct(v, this.lang(), digits);
  }

  /** Note : entière quand elle l'est, une décimale sinon (9 → « 9 », 9,5 → « 9,5 »). */
  mark(v: number): string {
    return this.fmtMark(v, this.lang());
  }

  /** Date longue : « 12 جانفي 2026 », « 12 janvier 2026 ». */
  date(iso: string): string {
    return this.fmtDate(iso, this.lang());
  }

  /** Date et heure, sur 24 h. */
  dateTime(iso: string): string {
    return this.fmtDateTime(iso, this.lang());
  }

  /* ── Codes de compétences ────────────────────────────────────── */

  /** Préfixe des codes de compétences : `C` partout, `م` en arabe. */
  codePrefix(): string {
    return this.t('competency.codePrefix');
  }

  /**
   * Code tel que le lecteur le voit : `C12` devient `م12` en arabe.
   *
   * Purement d'affichage. Le code brut reste la clé partout ailleurs (tri,
   * filtres, recherche, `data-code`), sans quoi une bascule de langue
   * déplacerait les lignes du tableau.
   */
  displayCode(code: string): string {
    const prefix = this.codePrefix();
    if (!prefix || prefix === 'C') return code;
    return String(code).replace(/^C/, prefix);
  }

  /**
   * Sens de lecture d'un code isolé.
   *
   * Un code mêle une lettre et des chiffres : en contexte RTL, `م12` se
   * retrouve rendu « 12م » si on laisse faire l'algorithme bidi. On force donc
   * le sens d'après le préfixe — latin donc LTR, arabe donc RTL.
   */
  codeDirection(): ReportDir {
    return /^[A-Za-z]/.test(String(this.codePrefix() || 'C')) ? 'ltr' : 'rtl';
  }

  /* ── Rouages ─────────────────────────────────────────────────── */

  /**
   * Résolution d'une clé, à l'identique du modèle.
   *
   * Chaîne : candidats de pluriel dans la langue courante → clé nue → anglais →
   * dernier recours ngx-translate → la clé rendue telle quelle.
   *
   * Le `||` en cascade est volontaire (et non `??`) : une valeur vide compte
   * pour une absence, exactement comme dans le modèle. Le repli anglais porte
   * sur la **clé nue**, jamais sur une variante de pluriel — l'anglais n'en
   * déclare que `_one`, le reste vit sur la clé de base.
   */
  private translateIn(
    lang: ReportLang,
    key: string,
    vars?: Record<string, unknown>,
  ): string {
    const full = `${REPORT_NAMESPACE}.${key}`;
    const count = vars?.['count'];
    let resolved: string | undefined;

    if (vars && typeof count === 'number') {
      const category = new Intl.PluralRules(lang).select(count);
      resolved =
        this.lookup(lang, `${full}_${category}`) ||
        (count === 0 ? this.lookup(lang, `${full}_zero`) : undefined) ||
        this.lookup(lang, `${full}_other`) ||
        this.lookup(lang, full);
    } else {
      resolved = this.lookup(lang, full);
    }

    if (resolved === undefined) resolved = this.lookup('en', full);
    if (resolved === undefined) resolved = this.lastResort(full);
    if (resolved === undefined) return key;

    if (!vars) return resolved;

    // Le modèle n'accepte que `{{nom}}`, sans espaces — plus strict que le
    // gabarit de ngx-translate, et c'est ce que contiennent les bundles.
    // Un marqueur sans variable correspondante reste **intact** : mieux vaut
    // voir `{{total}}` à l'écran qu'un « undefined » qui passerait inaperçu.
    return resolved.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
      Object.prototype.hasOwnProperty.call(vars, name)
        ? String(vars[name])
        : match,
    );
  }

  /**
   * Lecture d'une clé pointée dans le bundle d'une langue donnée.
   *
   * On interroge `translations` plutôt que `instant()` parce qu'il faut pouvoir
   * distinguer « absent de cette langue-ci » de « absent partout » : c'est ce
   * que `instant()` masque en repliant sur la langue par défaut (cf. l'en-tête
   * de la classe). Seule une **chaîne** est une traduction ; un nœud
   * intermédiaire n'en est pas une.
   */
  private lookup(lang: string, key: string): string | undefined {
    let node: unknown = this.translate.translations[lang];
    for (const part of key.split('.')) {
      if (node === null || typeof node !== 'object') return undefined;
      node = (node as Record<string, unknown>)[part];
    }
    return typeof node === 'string' ? node : undefined;
  }

  /**
   * Dernier recours, quand ni la langue courante ni l'anglais ne portent la clé.
   *
   * On laisse alors ngx-translate dérouler sa propre chaîne (langue par défaut,
   * `MissingTranslationHandler`). Il signale l'échec en renvoyant la clé
   * inchangée — c'est le seul signal qu'il expose. Un texte arabe vaut mieux
   * qu'une clé technique à l'écran, et c'est déjà le repli retenu ailleurs dans
   * l'application (`LocalizedLabelService`). Inatteignable tant que les trois
   * bundles `REPORT` restent complets.
   */
  private lastResort(full: string): string | undefined {
    const viaNgx: unknown = this.translate.instant(full);
    return typeof viaNgx === 'string' && viaNgx !== full ? viaNgx : undefined;
  }

  /**
   * Formatage des variables d'après la **forme** de l'objet reçu.
   *
   * `{n}` nombre, `{p}` pourcentage, `{mark}` note, `{k}` clé à traduire,
   * `{dt}` date et heure, `{d}` date ; toute autre valeur passe telle quelle.
   *
   * Un objet qui ne présente aucune de ces formes n'est **pas** reporté dans le
   * résultat : le marqueur correspondant restera visible dans le libellé, ce
   * qui rend l'erreur d'appel évidente au lieu d'afficher « [object Object] ».
   */
  private resolveVars(
    lang: ReportLang,
    vars?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    if (!vars) return undefined;

    const out: Record<string, unknown> = {};
    for (const name of Object.keys(vars)) {
      const value = vars[name];
      if (value && typeof value === 'object') {
        const shape = value as Record<string, unknown>;
        if ('n' in shape) out[name] = this.fmtNum(shape['n'] as number, lang);
        else if ('p' in shape) out[name] = this.fmtPct(shape['p'] as number, lang);
        else if ('mark' in shape) out[name] = this.fmtMark(shape['mark'] as number, lang);
        else if ('k' in shape) out[name] = this.translateIn(lang, String(shape['k']));
        else if ('dt' in shape) out[name] = this.fmtDateTime(String(shape['dt']), lang);
        else if ('d' in shape) out[name] = this.fmtDate(String(shape['d']), lang);
      } else {
        out[name] = value;
      }
    }
    return out;
  }

  /* ── Formateurs, langue explicite ────────────────────────────── */

  private numericLocale(lang: ReportLang): string {
    return NUMERIC_LOCALE[lang] || 'en-GB';
  }

  private fmtNum(value: number, lang: ReportLang, digits?: number): string {
    const d = digits || 0;
    return new Intl.NumberFormat(this.numericLocale(lang), {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(value);
  }

  private fmtPct(value: number, lang: ReportLang, digits?: number): string {
    const d = digits || 0;
    return new Intl.NumberFormat(this.numericLocale(lang), {
      style: 'percent',
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(value / 100);
  }

  private fmtMark(value: number, lang: ReportLang): string {
    return this.fmtNum(value, lang, Number.isInteger(value) ? 0 : 1);
  }

  private fmtDate(iso: string, lang: ReportLang): string {
    const date = new Date(iso);
    if (lang === 'ar') {
      // Composition manuelle : `Intl` rendrait les mois du Machrek (يناير…).
      const day = String(date.getDate()).padStart(2, '0');
      return `${day} ${AR_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
    }
    return new Intl.DateTimeFormat(this.numericLocale(lang), {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private fmtDateTime(iso: string, lang: ReportLang): string {
    const date = new Date(iso);
    if (lang === 'ar') {
      // L'heure passe par `Intl`, la date non : seuls les mois posaient problème.
      const time = new Intl.DateTimeFormat(this.numericLocale(lang), {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
      return `${this.fmtDate(iso, lang)} ${time}`;
    }
    return new Intl.DateTimeFormat(this.numericLocale(lang), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}

/** Toute langue inconnue retombe sur l'arabe, langue par défaut du produit. */
function normalizeLang(code: string | undefined | null): ReportLang {
  return SUPPORTED_LANGS.includes(code as ReportLang)
    ? (code as ReportLang)
    : 'ar';
}
